import { Interval } from '@aurora/shared-generated/types/graphql-schema-types';
import type { FormatMessage } from '@aurora/shared-types/texts';
import { getLog } from '@aurora/shared-utils/log';

const log = getLog(module);

/**
 *
 * @param dateObject
 * @returns Date in UTC time at 0.0.0.0 Hrs
 */
export const getUtcStartTime = (dateObject: Date) => {
  return new Date(
    Date.UTC(dateObject.getFullYear(), dateObject.getMonth(), dateObject.getDate(), 0, 0, 0, 0)
  );
};

/**
 *
 * @param dateObject
 * @returns Date in UTC time at 23.59.0.0 Hrs
 */
export const getUtcEndTime = (dateObject: Date) => {
  return new Date(
    Date.UTC(dateObject.getFullYear(), dateObject.getMonth(), dateObject.getDate(), 23, 59, 0, 0)
  );
};

/** Returns the date in MMMM dd, yyyy format for date range selection
 * @param dateObject_ Date which needs to format
 */
export function dateToShortFormat(dateObject_: Date): string {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  const day = dateObject_.getDate();
  const monthIndex = dateObject_.getMonth();
  const monthName = monthNames[monthIndex];
  const year = dateObject_.getFullYear();
  return `${monthName} ${day}, ${year}`;
}

/** Returns the timezone value in "short" format for date selection
 * @param dateObject_ Date which needs to format
 * @param timeZone timezone which need to format
 */
export function getTimeZoneShortFormat(dateObject_: Date, timeZone: string): string {
  return Intl.DateTimeFormat('en-us', {
    timeZone,
    timeZoneName: 'short'
  })
    .formatToParts(dateObject_)
    .find(part => part.type == 'timeZoneName').value;
}

/**
 * Get the date difference in days
 * @param startTime
 * @param endTime
 * @returns days in number
 */
export function dayDiff(startTime: number, endTime: number) {
  const diffTime = Math.abs(startTime - endTime);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * function to find the difference in years between current year and birthdate year
 *
 * @param birthDate - the date of birth
 * @returns number - the difference between current year and birth year
 */
export function diffYear(birthDate: Date): number {
  const currentDate = new Date();
  let yearsDiff = currentDate.getFullYear() - birthDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const birthMonth = birthDate.getMonth();
  const monthDiff = currentMonth - birthMonth;
  if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDate.getDate())) {
    yearsDiff = yearsDiff - 1;
  }
  return yearsDiff;
}

/**
 * Returns true if the provided date matches today's day, month, and year
 *
 * @param date the date to verify
 */
export function isToday(date: Date): boolean {
  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

/**
 * Returns converted date to the given timezone
 *
 * @param dateObject_ Date whose timezone needs to be converted
 * @param timeZone Timezone to which the date needs to be converted
 * @returns converted date to the given timezone
 */
export function convertTimezone(dateObject_: Date, timeZone: string): string {
  const intlDateObject = new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  });
  return intlDateObject.format(dateObject_);
}

/**
 * Get the event ongoing status
 * @param startTime
 * @param endTime
 * @param timeDelta
 * @returns if the event is ongoing
 */
export function isEventOngoing(startTime: number, endTime: number, timeDelta = 0) {
  return (
    Date.now() >= new Date(startTime).getTime() - timeDelta &&
    Date.now() < new Date(endTime).getTime() + timeDelta
  );
}

/**
 * Get the event end status
 * @param endTime
 * @returns if the event is ended
 */
export function isEventEnded(endTime: Date) {
  return endTime.getTime() < Date.now();
}

/**
 * Get the date difference in hours
 * @param time
 * @returns difference betweeen provided time and current time
 */
export function getDiffInHours(time: number): number {
  return Math.abs(Date.now() - new Date(time).getTime()) / 3600000;
}

/**
 * Return interval on the basis of the days
 * @param startTime
 * @param endTime
 * @returns intervals
 */
export const intervalCalculator = (startTime: number, endTime: number) => {
  const selectedDateRange = dayDiff(startTime, endTime);
  if (selectedDateRange < 90) {
    return Interval.OneHour;
  } else {
    return Interval.OneDay;
  }
};

/**
 * Given an epoch time value and a timezone string, returns a Date object with respect to the provided timezone.
 *
 * @param timezone timezone to convert the date to.
 * @param dateTime Epoch time value.
 */
export function getLocalTime(timezone: string, dateTime = new Date()): Date {
  try {
    return new Date(
      new Date(dateTime).toLocaleString('en-US', {
        timeZone: timezone
      })
    );
  } catch (error) {
    log.error(error);
    return new Date(dateTime);
  }
}

/**
 * Removes or adds the users timezone offset + browser timezone for a given date string.
 * @param dateString - string to be normalised for calendar component
 * @param add - whether to add or remove timezone for normalising
 * @param userTimeZone - the timezone value
 */
export function normaliseTimezoneOffset(
  dateString: string | Date,
  add: boolean,
  userTimeZone: string
): Date {
  /**
   * AWS servers hosted in us-west-2 have their time zone value set as US/Pacific-New.
   * US/Pacific-New is no longer valid as mentioned in https://stackoverflow.com/a/64625291.
   * It was an alias of US/Pacific, so we are going to use "US/Pacific" instead. This
   * change is added here because this function is called from multiple places, and the value of
   * userTimeZone is the server's time zone.
   **/
  const formattedTimeZone =
    userTimeZone.toLowerCase() === 'us/pacific-new' ? 'US/Pacific' : userTimeZone;

  const normalisedDate = new Date(dateString);
  const userTimeZoneOffset =
    new Date(normalisedDate.toLocaleString('en-US', { timeZone: formattedTimeZone })).getTime() -
    new Date(normalisedDate.toLocaleString('en-US', { timeZone: 'UTC' })).getTime();

  const timeZoneComponent = userTimeZoneOffset + normalisedDate.getTimezoneOffset() * 60000;
  normalisedDate.setTime(normalisedDate.getTime() + (add ? timeZoneComponent : -timeZoneComponent));
  return normalisedDate;
}

/**
 * Convert milliseconds to human readable format.
 * @param ms milliseconds
 * @param formatMessage the format message
 * @param textKey the text key
 *
 * @returns the human readable format
 */
export function convertMilliSecondsToTime(
  ms: number,
  formatMessage: FormatMessage,
  textKey: string
): string {
  const second = 1000;
  const minute = 60 * second;
  const hour = 60 * minute;
  const day = 24 * hour;
  const year = 365 * day;

  let unit = 'ms';
  let value = ms;

  if (ms >= second) {
    unit = 'sec';
    value = Math.floor(ms / second);
  }
  if (ms >= minute) {
    unit = 'min';
    value = Math.floor(ms / minute);
  }
  if (ms >= hour) {
    unit = 'hour';
    value = Math.floor(ms / hour);
  }
  if (ms >= day) {
    unit = 'day';
    value = Math.floor(ms / day);
  }
  if (ms >= year) {
    unit = 'year';
    value = Math.floor(ms / year);
  }

  return formatMessage(textKey, { unit, value });
}
